import type EventBase from '@/model/EventClass/EventBase'
import {
  findStructureBlockEndIndex,
  getEventSelectionRange,
  isStructureHeaderCode,
  isStructureReadonlyCode,
} from '@/events/list/structureRules'

/**
 * 解析当前可用于复制/剪切/粘贴/删除/拖拽的事件下标列表。
 * 以 UI 维护的 selectedIndices 为唯一来源；仅在选中集为空但仍有主选中行时，
 * 按主选中行的点选范围补全（修复选中集与主行不同步的偶发状态）。
 */
export function resolveSelectedEventIndices(
  list: EventBase[],
  selectedIndices: ReadonlySet<number>,
  primaryIndex: number | null,
): number[] {
  const normalized: number[] = []
  for (const index of selectedIndices) {
    if (index >= 0 && index < list.length) normalized.push(index)
  }
  if (normalized.length) {
    normalized.sort((a, b) => a - b)
    return normalized
  }
  if (primaryIndex === null || primaryIndex < 0 || primaryIndex >= list.length) {
    return []
  }
  const range = getEventSelectionRange(list, primaryIndex)
  if (range.start < 0 || range.end < range.start) {
    return [primaryIndex]
  }
  const fallback: number[] = []
  for (let index = range.start; index <= range.end; index += 1) {
    fallback.push(index)
  }
  return fallback
}

/**
 * 删除/剪切后应选中的行：优先保留原首条删除位置的前一行（同下标），列表空则 null。
 */
export function resolveSelectionAfterRemove(
  removedIndices: number[],
  listLengthAfterRemove: number,
): number | null {
  if (listLengthAfterRemove <= 0) return null
  const firstRemoved = removedIndices[0] ?? 0
  return Math.min(firstRemoved, listLengthAfterRemove - 1)
}

/**
 * 选中集是否含未随结构头一并选中的只读结构壳行（选项项/结束/以上反复等）。
 */
export function hasBlockedReadonlyStructureInSelection(
  events: EventBase[],
  indices: Iterable<number>,
): boolean {
  const selected = new Set(indices)
  const coveredReadonly = new Set<number>()
  for (const index of selected) {
    if (index < 0 || index >= events.length) continue
    if (!isStructureHeaderCode(events[index]?.Code ?? -1)) continue
    const end = findStructureBlockEndIndex(events, index)
    for (let cursor = index + 1; cursor <= end; cursor += 1) {
      if (isStructureReadonlyCode(events[cursor]?.Code ?? -1)) coveredReadonly.add(cursor)
    }
  }
  for (const index of selected) {
    if (index < 0 || index >= events.length) continue
    if (isStructureReadonlyCode(events[index]?.Code ?? -1) && !coveredReadonly.has(index)) return true
  }
  return false
}
